import * as React from "react";
import {
    Popover,
    Box,
    Button,
} from "@mui/material";
import {ReassignCases} from "../ReassignCases/index";
import {Assignment, ChangeCircle, ChangeCircleOutlined, Undo} from "@mui/icons-material";
import { ExcludeCases } from "../ExcludeCases/index";
import { RevertApproval } from "../RevertApproval/index";
import { MoveToClosedDialog, CqaWorkItemRow } from "../MoveToClosedDialog";
import { Block, RestartAlt } from "@mui/icons-material";
import { managerViews } from "../../organisms/workItemsTable";
import { WorkitemStatus } from "../ProjectSelectColumn/types";
import { apiBulkUpdateGraderWorkitems } from "../../../utils/api";
import { SnackbarContext } from "../../../context/AlertsContext";
import { useCaseTransferPermissionsConfig } from "../../../hooks/useCaseTransferPermissionsConfig";

type Props = {
    client: any;
    anchorEl: any;
    handleClose: any;
    handleMove: any;
    coders: any;
    selected: CqaWorkItemRow[];
    allSelected?: boolean;
    isChangeOwner?: boolean;
    navSubviewId?: string | null;
    activeView:string,
    handleAfterExclusion: () => void,
    serviceLine:string,
    isQualityAssessment?: boolean;
    isCQAManager?: boolean;
    handleAfterRevert?: (succeededIds?: number[]) => void;
    cqaWorkitemStatus?: string;
};

export const ActionMenu = ({
    anchorEl,
    selected,
    handleClose,
    handleMove,
    coders,
    client,
    allSelected,
    activeView,
    navSubviewId,
    handleAfterExclusion,
    serviceLine,
    isQualityAssessment,
    isCQAManager,
    handleAfterRevert,
    cqaWorkitemStatus,
    ...props
}: Props) => {

    const [openReassign, setOpenReassign] = React.useState(false);
    const [openExcludeCases, setOpenExcludeCases] = React.useState(false);
    const [openRevertApproval, setOpenRevertApproval] = React.useState(false);
    const [isMarkingAuditComplete, setIsMarkingAuditComplete] = React.useState(false);
    const handleOpenReassign = () => setOpenReassign(true);
    const handleCloseReassign = () => setOpenReassign(false);
    const handleOpenExcludeCases = () => setOpenExcludeCases(true);
    const handleCloseExcludeCases = () => setOpenExcludeCases(false);
    const handleOpenRevertApproval = () => setOpenRevertApproval(true);
    const handleCloseRevertApproval = () => setOpenRevertApproval(false);
    const [openMoveToClosed, setOpenMoveToClosed] = React.useState(false);
    const [openRollbackClose, setOpenRollbackClose] = React.useState(false);
    const { openSnackbar } = React.useContext(SnackbarContext);

    const handleApproveAudit = async () => {
        const auditRows = (selected ?? []).filter(
            (item: any) => item.qa_status === WorkitemStatus.CQA_AUDIT
        );
        if (auditRows.length === 0) return;
        setIsMarkingAuditComplete(true);
        try {
            await apiBulkUpdateGraderWorkitems(
                auditRows.map((item: any) => ({
                    sdCaseId: item.sd_case_id || item.case_id,
                    status: WorkitemStatus.CQA_AUDIT_COMPLETE,
                }))
            );
            openSnackbar(
                `${auditRows.length} audit item${auditRows.length > 1 ? "s" : ""} approved.`,
                "success"
            );
            handleAfterRevert?.();
            handleClose?.();
        } catch (e: any) {
            console.error("apiBulkUpdateGraderWorkitems (audit approve) failed", e);
            openSnackbar("Failed to approve audit items. Please try again.", "error");
        } finally {
            setIsMarkingAuditComplete(false);
        }
    };

    const { canShowCaseTransferOption } = useCaseTransferPermissionsConfig();

    const handleReassign = (selected, assignee,is_manual_assignment) => {
        handleMove(selected, assignee,is_manual_assignment);
        handleCloseReassign();
    }
    return (
        <Popover
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
        >
            <Box
                sx={{
                    maxHeight: "80vh",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "left",
                    alignItems:"flex-start"
            }}
            >
                {/* CQA-only bulk actions when in Quality Assessment mode */}
                {isQualityAssessment ? (
                    <>
                        {isCQAManager && (
                            <>
                                {/* Reassign is not valid for force-closed (CQA_CLOSED) items; hide it on
                                    the Closed tab so closed workitems can't enter the reassign flow. */}
                                {cqaWorkitemStatus !== WorkitemStatus.CQA_CLOSED && (
                                    <Button
                                        onClick={handleOpenReassign}
                                        disableRipple={true}
                                        sx={{ color: "black", padding: "10px"}}
                                    >
                                        <Assignment style={{marginRight: "0.5em"}}/>
                                        {"Reassign"}
                                    </Button>
                                )}
                                
                                {(cqaWorkitemStatus === WorkitemStatus.CQA_APPROVED || cqaWorkitemStatus === WorkitemStatus.CQA_COMPLETE) && (
                                    <Button
                                        onClick={handleOpenRevertApproval}
                                        disableRipple={true}
                                        sx={{ color: "black", padding: "10px"}}
                                    >
                                        <Undo style={{marginRight: "0.5em"}}/>
                                        {cqaWorkitemStatus === WorkitemStatus.CQA_COMPLETE ? "Revert Submission" : "Revert Approval"}
                                    </Button>
                                )}
                                {/* The Assigned-to-Me manager view is filtered to IN_CQA rows
                                    (assignee=MY_CASES + workitem.qa_status=IN_CQA), so a manager must be
                                    able to force-close their own assigned cases there too. */}
                                {(cqaWorkitemStatus === WorkitemStatus.IN_CQA ||
                                    cqaWorkitemStatus === WorkitemStatus.ASSIGNED_TO_ME) && (
                                    <Button
                                        onClick={() => setOpenMoveToClosed(true)}
                                        disableRipple={true}
                                        sx={{ color: "black", padding: "10px"}}
                                    >
                                        <Block style={{marginRight: "0.5em"}}/>
                                        {"Move to Closed"}
                                    </Button>
                                )}
                                {cqaWorkitemStatus === WorkitemStatus.CQA_CLOSED && (
                                    <Button
                                        onClick={() => setOpenRollbackClose(true)}
                                        disableRipple={true}
                                        sx={{ color: "black", padding: "10px"}}
                                    >
                                        <RestartAlt style={{marginRight: "0.5em"}}/>
                                        {"Rollback Close"}
                                    </Button>
                                )}
                                {cqaWorkitemStatus === WorkitemStatus.CQA_AUDIT && (
                                    <Button
                                        onClick={handleApproveAudit}
                                        disableRipple={true}
                                        disabled={isMarkingAuditComplete || !(selected?.some((i: any) => i.qa_status === WorkitemStatus.CQA_AUDIT))}
                                        sx={{ color: "black", padding: "10px"}}
                                    >
                                        <ChangeCircle style={{marginRight: "0.5em"}}/>
                                        {isMarkingAuditComplete ? "Marking..." : "Mark Audit Complete"}
                                    </Button>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    /* Regular worklist bulk actions */
                    <>
                       {canShowCaseTransferOption(activeView, navSubviewId) && (
                            <Button
                                onClick={handleOpenReassign}
                                disableRipple={true}
                                sx={{ color: "black", padding: "10px"}}
                            >
                                <Assignment style={{marginRight: "0.5em"}}/>
                                {props?.isChangeOwner ? "Move to a different coder" : "Reassign"}
                            </Button>
                        )}
                        <Button
                            onClick={handleOpenExcludeCases}
                            disableRipple={true}
                            sx={{ color: "black", padding: "10px"}}
                        >
                            <Assignment style={{marginRight: "0.5em"}}/>
                            {"Exclude"}
                        </Button>
                    </>
                )}
                <ReassignCases
                    selected={selected}
                    open={openReassign}
                    handleClose={handleCloseReassign}
                    handleMove={handleReassign}
                    coders={coders}
                    allSelected={allSelected}
                />
                <ExcludeCases
                    selected={selected}
                    open={openExcludeCases}
                    handleClose={handleCloseExcludeCases}
                    handleAfterExclusion={handleAfterExclusion}
                    serviceLine={serviceLine}
                />
                <RevertApproval
                    selected={selected}
                    open={openRevertApproval}
                    handleClose={handleCloseRevertApproval}
                    handleAfterRevert={handleAfterRevert || (() => {})}
                    isQualityAssessment={isQualityAssessment}
                    cqaWorkitemStatus={cqaWorkitemStatus}
                />
                <MoveToClosedDialog
                    selected={selected}
                    allSelected={allSelected}
                    open={openMoveToClosed}
                    handleClose={() => setOpenMoveToClosed(false)}
                    handleAfterClose={handleAfterRevert || (() => {})}
                    mode="close"
                />
                <MoveToClosedDialog
                    selected={selected}
                    allSelected={allSelected}
                    open={openRollbackClose}
                    handleClose={() => setOpenRollbackClose(false)}
                    handleAfterClose={handleAfterRevert || (() => {})}
                    mode="rollback"
                />
            </Box>
        </Popover>
    )
}
